import React, { useState } from 'react';
import { Play, Eye, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { Button } from '../components/ui';
import { 
  previewMigration, 
  migrateAllPropsToWasabi, 
  validateMigration 
} from '../utils/migrateToWasabi';

const WasabiMigrationTool = () => {
  const [step, setStep] = useState('preview');
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [migrationResults, setMigrationResults] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [error, setError] = useState(null);

  const runPreview = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Running migration preview...');
      
      const preview = await previewMigration();
      setPreviewData(preview);
      setStep('preview-results');
      
    } catch (err) {
      console.error('Preview failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runMigration = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🚀 Starting migration...');
      
      const results = await migrateAllPropsToWasabi();
      setMigrationResults(results);
      setStep('migration-complete');
      
    } catch (err) {
      console.error('Migration failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runValidation = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🧪 Running validation...');
      
      const results = await validateMigration(10);
      setValidationResults(results);
      setStep('validation-complete');
      
    } catch (err) {
      console.error('Validation failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-2">Wasabi Migration Tool</h1>
        <p className="text-gray-600">
          Migrate existing prop images from local filenames to Wasabi cloud URLs
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Error</span>
          </div>
          <p className="text-red-700 mt-2">{error}</p>
        </div>
      )}

      {/* Step 1: Preview */}
      {step === 'preview' && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Step 1: Preview Migration
          </h2>
          <p className="text-gray-600 mb-4">
            First, let's see what props need to be migrated without making any changes.
          </p>
          <Button 
            onClick={runPreview} 
            disabled={loading}
            variant="primary"
            className="flex items-center gap-2"
          >
            {loading ? <Loader className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            {loading ? 'Analyzing...' : 'Preview Migration'}
          </Button>
        </div>
      )}

      {/* Step 1 Results: Preview Results */}
      {step === 'preview-results' && previewData && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Migration Preview Results</h2>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">
                {previewData.filter(p => p.action === 'migrate').length}
              </div>
              <div className="text-sm text-blue-800">Props to Migrate</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {previewData.filter(p => p.action === 'skip' && p.reason.includes('Wasabi')).length}
              </div>
              <div className="text-sm text-green-800">Already Migrated</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-600">
                {previewData.filter(p => p.action === 'skip' && p.reason.includes('No images')).length}
              </div>
              <div className="text-sm text-gray-800">No Images</div>
            </div>
          </div>

          {/* Show props that will be migrated */}
          <div className="mb-6">
            <h3 className="font-medium mb-3">Props to be migrated:</h3>
            <div className="max-h-60 overflow-y-auto space-y-2">
              {previewData
                .filter(p => p.action === 'migrate')
                .slice(0, 10)
                .map(prop => (
                <div key={prop.id} className="border rounded p-3 text-sm">
                  <div className="font-medium">{prop.name}</div>
                  <div className="text-gray-600 mt-1">
                    <div>From: {prop.current.join(', ')}</div>
                    <div>To: {prop.proposed[0]} {prop.proposed.length > 1 ? `+ ${prop.proposed.length - 1} more` : ''}</div>
                  </div>
                </div>
              ))}
              {previewData.filter(p => p.action === 'migrate').length > 10 && (
                <div className="text-center text-gray-500 text-sm">
                  ... and {previewData.filter(p => p.action === 'migrate').length - 10} more props
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={runMigration} 
              disabled={loading}
              variant="primary"
              className="flex items-center gap-2"
            >
              {loading ? <Loader className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {loading ? 'Migrating...' : 'Start Migration'}
            </Button>
            <Button 
              onClick={() => setStep('preview')} 
              variant="secondary"
            >
              Back to Preview
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Migration Complete */}
      {step === 'migration-complete' && migrationResults && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Migration Complete!
          </h2>
          
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{migrationResults.total}</div>
              <div className="text-sm text-blue-800">Total Props</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{migrationResults.migrated}</div>
              <div className="text-sm text-green-800">Migrated</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">{migrationResults.skipped}</div>
              <div className="text-sm text-yellow-800">Skipped</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">{migrationResults.failed}</div>
              <div className="text-sm text-red-800">Failed</div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800">
              ✅ Migration completed successfully! Your props should now display images from Wasabi cloud storage.
            </p>
          </div>

          <Button 
            onClick={runValidation} 
            disabled={loading}
            variant="primary"
            className="flex items-center gap-2"
          >
            {loading ? <Loader className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            {loading ? 'Testing...' : 'Test Image URLs'}
          </Button>
        </div>
      )}

      {/* Step 3: Validation Complete */}
      {step === 'validation-complete' && validationResults && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Validation Results</h2>
          
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-600">{validationResults.tested}</div>
              <div className="text-sm text-blue-800">URLs Tested</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">{validationResults.working}</div>
              <div className="text-sm text-green-800">Working</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-red-600">{validationResults.broken}</div>
              <div className="text-sm text-red-800">Broken</div>
            </div>
          </div>

          {validationResults.working === validationResults.tested ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 font-medium">
                🎉 All tested URLs are working! Your Wasabi migration is successful.
              </p>
              <p className="text-green-700 text-sm mt-2">
                Your props should now display images from Wasabi cloud storage.
              </p>
            </div>
          ) : (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 font-medium">
                ⚠️ Some URLs may not be working properly.
              </p>
              <div className="mt-3 space-y-2">
                {validationResults.details.map((result, index) => (
                  <div key={index} className={`text-sm p-2 rounded ${
                    result.working ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {result.working ? '✅' : '❌'} {result.propName}: {result.url.split('/').pop()}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Console Log Instructions */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium mb-2">💡 Tip: Watch the Console</h3>
        <p className="text-sm text-gray-600">
          Open your browser's developer console (F12) to see detailed migration progress and any errors.
        </p>
      </div>
    </div>
  );
};

export default WasabiMigrationTool;
